import { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  computeAuditDiff,
  diffStringArrays,
  formatAuditDiffValue,
  summarizeAuditDiff,
  type AuditDiffChange,
} from '../../../lib/auditDiff';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

/** Side-by-side before/after view of one audit row, one line per changed path. */
export function AuditDiffViewer({ before, after }: { before: unknown; after: unknown }) {
  const theme = useTheme();
  const [showUnchanged, setShowUnchanged] = useState(false);
  const rows = useMemo(() => computeAuditDiff(before, after), [before, after]);
  const summary = summarizeAuditDiff(rows);
  const visible = showUnchanged ? rows : rows.filter((r) => r.change !== 'unchanged');

  const tint = (change: AuditDiffChange, side: 'before' | 'after') => {
    if (change === 'changed') return alpha(theme.palette.warning.main, 0.12);
    if (change === 'added' && side === 'after') return alpha(theme.palette.success.main, 0.14);
    if (change === 'removed' && side === 'before') return alpha(theme.palette.error.main, 0.12);
    return 'transparent';
  };

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No before/after state recorded for this event.
      </Typography>
    );
  }

  return (
    <Box>
      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Chip size="small" color="warning" variant="outlined" label={`${summary.changed} changed`} />
        <Chip size="small" color="success" variant="outlined" label={`${summary.added} added`} />
        <Chip size="small" color="error" variant="outlined" label={`${summary.removed} removed`} />
        <FormControlLabel
          sx={{ ml: 'auto' }}
          control={<Switch size="small" checked={showUnchanged} onChange={(_, v) => setShowUnchanged(v)} />}
          label={<Typography variant="caption">Show unchanged ({summary.unchanged})</Typography>}
        />
      </Stack>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 560 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '24%' }}>Field</TableCell>
              <TableCell sx={{ width: '38%' }}>Before</TableCell>
              <TableCell sx={{ width: '38%' }}>After</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((row) => {
              const arr = row.change === 'changed' ? diffStringArrays(row.before, row.after) : null;
              return (
                <TableRow key={row.path}>
                  <TableCell sx={{ fontFamily: MONO, fontSize: '0.75rem', wordBreak: 'break-all', verticalAlign: 'top' }}>
                    {row.path}
                  </TableCell>
                  {(['before', 'after'] as const).map((side) => (
                    <TableCell
                      key={side}
                      sx={{ bgcolor: tint(row.change, side), verticalAlign: 'top', fontFamily: MONO, fontSize: '0.75rem' }}
                    >
                      <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', font: 'inherit' }}>
                        {formatAuditDiffValue(side === 'before' ? row.before : row.after)}
                      </Box>
                      {arr && side === 'after' && (arr.added.length > 0 || arr.removed.length > 0) && (
                        <Stack direction="row" gap={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                          {arr.added.map((k) => (
                            <Chip key={`+${k}`} size="small" color="success" variant="outlined" label={`+ ${k}`} />
                          ))}
                          {arr.removed.map((k) => (
                            <Chip key={`-${k}`} size="small" color="error" variant="outlined" label={`− ${k}`} />
                          ))}
                        </Stack>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary">
                    Nothing changed.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
